/**
 * /admin-users — Admin user CRUD (Story 7.11, admin-only).
 *
 * If the current user is not an admin, we render a 403-style banner and
 * never fetch — the server would 403 anyway, but this preserves the UX so
 * the user sees a friendly message instead of a toast chain.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Power, KeyRound } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/sonner';
import { ApiError, apiClient } from '@/lib/api';
import { formatDateTime } from '@/lib/format-date';
import { UserForm, type AdminRole, type UserFormValue } from '@/components/users/UserForm';
import { OneTimePasswordDialog } from '@/components/users/OneTimePasswordDialog';
import { useAuthStore } from '@/stores/auth';

interface AdminUserDto {
  id: string;
  email: string;
  name: string;
  role: AdminRole;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  data: AdminUserDto[];
  page: number;
  pageSize: number;
  total: number;
}

interface CreateResponse {
  user: AdminUserDto;
  temporaryPassword: string;
}

interface ResetResponse {
  user: AdminUserDto;
  temporaryPassword: string;
}

const ROLE_BADGE: Record<AdminRole, string> = {
  admin: 'bg-red-500/20 text-red-700 dark:text-red-300',
  operator: 'bg-blue-500/20 text-blue-700 dark:text-blue-300',
  viewer: 'bg-muted text-muted-foreground',
};

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } };
    const code = body?.error?.code;
    if (code === 'EMAIL_TAKEN') return 'Já existe um usuário com esse email.';
    if (code === 'USER_NOT_FOUND') return 'Usuário não encontrado.';
    if (code === 'LAST_ADMIN_DELETE_BLOCKED') {
      return 'Não é possível desativar o único admin ativo do sistema.';
    }
    if (code === 'INSUFFICIENT_ROLE') return 'Apenas administradores.';
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const isAdmin = currentUser?.role === 'admin';

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<AdminUserDto | null>(null);
  const [tempPw, setTempPw] = useState<{ email: string; password: string } | null>(null);

  const listQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => apiClient.get<ListResponse>('/admin/users?page=1&pageSize=100'),
    enabled: isAdmin,
  });

  const createMutation = useMutation({
    mutationFn: (body: UserFormValue) =>
      apiClient.post<CreateResponse>('/admin/users', {
        email: body.email,
        name: body.name || undefined,
        role: body.role,
      }),
    onSuccess: (res) => {
      toast.success('Usuário criado.');
      setCreateOpen(false);
      setTempPw({ email: res.user.email, password: res.temporaryPassword });
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao criar usuário.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<UserFormValue> }) =>
      apiClient.put<AdminUserDto>(`/admin/users/${id}`, body),
    onSuccess: () => {
      toast.success('Usuário atualizado.');
      setEditTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao atualizar usuário.')),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete<AdminUserDto>(`/admin/users/${id}`),
    onSuccess: () => {
      toast.success('Usuário desativado.');
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao desativar usuário.')),
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => apiClient.post<ResetResponse>(`/admin/users/${id}/reset-password`),
    onSuccess: (res) => {
      toast.success('Senha resetada.');
      setTempPw({ email: res.user.email, password: res.temporaryPassword });
      void queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => toast.error(humanizeError(err, 'Falha ao resetar senha.')),
  });

  if (!isAdmin) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        </div>
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Apenas administradores têm acesso a essa área.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">
            Administradores, operadores e viewers do painel.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1" aria-hidden />
          Novo usuário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Usuários</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground">Carregando…</div>
          ) : listQuery.error ? (
            <div className="p-6 text-sm text-destructive">
              {humanizeError(listQuery.error, 'Falha ao carregar usuários.')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" role="table">
                <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Nome</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Ativo</th>
                    <th className="px-4 py-2">Último login</th>
                    <th className="px-4 py-2 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listQuery.data && listQuery.data.data.length > 0 ? (
                    listQuery.data.data.map((u) => (
                      <tr key={u.id} className="border-b last:border-b-0">
                        <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
                        <td className="px-4 py-2">{u.name}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_BADGE[u.role]}`}
                          >
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-2">{u.active ? 'Sim' : 'Não'}</td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {formatDateTime(u.lastLoginAt) || '—'}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditTarget(u)}
                              aria-label={`Editar ${u.email}`}
                            >
                              <Pencil aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={resetMutation.isPending}
                              onClick={() => {
                                if (confirm(`Resetar a senha de ${u.email}?`)) {
                                  resetMutation.mutate(u.id);
                                }
                              }}
                              aria-label={`Resetar senha ${u.email}`}
                            >
                              <KeyRound aria-hidden />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={!u.active || deactivateMutation.isPending}
                              onClick={() => {
                                if (confirm(`Desativar ${u.email}?`)) {
                                  deactivateMutation.mutate(u.id);
                                }
                              }}
                              aria-label={`Desativar ${u.email}`}
                            >
                              <Power aria-hidden />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        Nenhum usuário encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo usuário</DialogTitle>
            <DialogDescription>
              Uma senha temporária será gerada e mostrada após a criação.
            </DialogDescription>
          </DialogHeader>
          <UserForm
            mode="create"
            submitting={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
            onSubmit={(next) => createMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editTarget !== null} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar usuário</DialogTitle>
            <DialogDescription>Email não pode ser alterado após a criação.</DialogDescription>
          </DialogHeader>
          {editTarget ? (
            <UserForm
              mode="edit"
              submitting={updateMutation.isPending}
              initial={{
                email: editTarget.email,
                name: editTarget.name,
                role: editTarget.role,
                active: editTarget.active,
              }}
              onCancel={() => setEditTarget(null)}
              onSubmit={(next) => {
                const { email: _email, ...rest } = next;
                void _email;
                updateMutation.mutate({ id: editTarget.id, body: rest });
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <OneTimePasswordDialog
        open={tempPw !== null}
        email={tempPw?.email ?? ''}
        temporaryPassword={tempPw?.password ?? ''}
        onClose={() => setTempPw(null)}
      />
    </div>
  );
}

export default AdminUsersPage;
